import React, { JSX } from 'react';
import SEO from 'components/seo';
import { Box, Container, Heading, Text, VStack, HStack, Icon, Grid, Flex } from '@chakra-ui/react';
import { Sparkles, AtSign, Wand2 } from 'lucide-react';
import { ROUTES } from 'services/shared/routerService';
import { CORPORATE } from 'services/shared/imageService';
import NextImage from 'next/image';
import CtaButton from 'components/CtaButton';

const cast = [
	{
		name: 'joe',
		role: 'The confident lead',
		img: CORPORATE.CHARACTER_1,
		tone: 'Confident · Warm · English',
		prompt: '@joe drives a vintage BMW through Hollywood Hills',
	},
	{
		name: 'mia',
		role: 'The editorial muse',
		img: CORPORATE.CHARACTER_2,
		tone: 'Calm · Refined · English',
		prompt: '@mia reads a book in a cozy library',
	},
	{
		name: 'kai',
		role: 'The high-energy creator',
		img: CORPORATE.CHARACTER_4,
		tone: 'Playful · Bold · English',
		prompt: '@kai goes jogging in the park',
	},
];

const benefits = [
	{
		t: 'Face-locked consistency',
		d: 'Upload one reference photo. Every future render keeps the same face, build and signature look — frame after frame, scene after scene.',
	},
	{
		t: 'Personality you can dial in',
		d: "Tune mood, humor, energy, warmth and sentence length on simple sliders. Your avatar speaks and moves with a voice that's unmistakably theirs.",
	},
	{
		t: 'Summon them with @name',
		d: 'Write a prompt like `@joe drives a vintage BMW through Hollywood Hills`. The avatars you build become a creative shorthand you can use.',
	},
	{
		t: 'A reusable set of characters',
		d: 'Build recurring characters for content, storytelling and creative projects.',
	},
];

const useCases = [
	{
		t: 'Content at scale',
		d: 'Create dozens of native-feeling content pieces featuring the same recognisable characters — without managing real talent.',
	},
	{
		t: 'Character Founder & spokesperson content persona content',
		d: 'Turn one photo into an always-on character that can appear in any content, any scene, any moment.',
	},
	{
		t: 'Episodic creative storytelling',
		d: 'Use recurring characters across your content. Viewers remember faces — your content will be more memorable.',
	},
	{
		t: 'Multilingual localization',
		d: 'Switch language on the same avatar and create localized creative for every market without re-casting talent.',
	},
];

function Index(): JSX.Element {
	return (
		<Box bg="white" position="relative" minH="100vh" overflowX="hidden">
			<SEO
				title="AI Avatars — AI Avatar Image & Video Generator | Imagemotion"
				description="Create AI avatars for your Images & Videos. Build a digital cast with our AI avatar image & Video generator — perfect for content creation, storytelling and visual projects."
			/>

			{/* Background Patterns */}
			<Box
				pointerEvents="none"
				position="absolute"
				inset={0}
				opacity={0.05}
				style={{
					backgroundImage: 'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)',
					backgroundSize: '28px 28px',
				}}
			/>

			{/* HERO */}
			<Box as="header" position="relative" zIndex={10} pt={{ base: 10, lg: 20 }} pb={{ base: 16, lg: 24 }}>
				<Container maxW="1400px" px={8}>
					<Grid templateColumns={{ base: '1fr', lg: '1.05fr 1fr' }} gap={12} alignItems="center">
						<VStack align="start" gap={0}>
							<HStack
								bg="gray.50"
								border="1px solid"
								borderColor="gray.100"
								px={3}
								py={1}
								borderRadius="full"
								gap={2}
								mb={5}
							>
								<Icon as={Sparkles} boxSize={3} color="primary" />
								<Text fontSize="11px" fontWeight="500" color="gray.500" letterSpacing="tight">
									New · AI Avatars
								</Text>
							</HStack>

							<Heading
								as="h1"
								fontSize={{ base: '40px', md: '72px', lg: '80px' }}
								fontWeight={400}
								lineHeight={1}
								letterSpacing="-0.04em"
								mb={5}
							>
								Digital characters.
								<br />
								<Text as="span" color="gray.500">
									Consistent every time.
								</Text>
							</Heading>

							<Text fontSize="17px" color="gray.500" maxW="xl" mb={7} lineHeight="1.5">
								Build a reusable set of AI avatars with locked appearance, voices and personalities for
								your creative projects. Reference any of them in a prompt with{' '}
								<Box
									as="code"
									bg="blackAlpha.50"
									px={1.5}
									py={0.5}
									borderRadius="md"
									fontFamily="mono"
									fontSize="14px"
									color="black"
								>
									@name
								</Box>{' '}
								and create content that feels consistent.
							</Text>

							<Flex wrap="wrap" align="center" gap={3}>
								<CtaButton href={ROUTES.REGISTER} showIcon hoverScale shadow="xl">
									Build your first avatar
								</CtaButton>
								{/*<Text fontSize="13.5px" fontWeight="500" color="gray.500" px={1}>*/}
								{/*	Free · No credit card required*/}
								{/*</Text>*/}
							</Flex>
						</VStack>

						{/* Visual: scene render */}
						<Box position="relative">
							<Box
								position="relative"
								overflow="hidden"
								borderRadius="3xl"
								border="1px solid"
								borderColor="gray.100"
								shadow="2xl"
								bg="gray.50"
							>
								<Box aspectRatio="1280/896" position="relative">
									<NextImage
										src={CORPORATE.CHARACTER_BMW_HOLLYWOOD}
										alt="AI avatar scene"
										fill
										style={{ objectFit: 'cover' }}
									/>
								</Box>
								<Box
									position="absolute"
									insetX={0}
									bottom={0}
									bg="linear-gradient(to top, rgba(0,0,0,0.75), transparent)"
									p={5}
								>
									<Text fontFamily="mono" fontSize="13px" color="whiteAlpha.900">
										@joe drives a vintage BMW through Hollywood Hills
									</Text>
								</Box>
							</Box>

							{/* Floating reference portrait */}
							<Box
								display={{ base: 'none', lg: 'block' }}
								position="absolute"
								top="-24px"
								left="-24px"
								w="80px"
								h="80px"
								overflow="hidden"
								borderRadius="2xl"
								border="1px solid"
								borderColor="gray.100"
								shadow="2xl"
								bg="white"
							>
								<NextImage
									src={CORPORATE.CHARACTER_1}
									alt="@joe reference portrait"
									fill
									style={{ objectFit: 'cover' }}
								/>
							</Box>
						</Box>
					</Grid>
				</Container>
			</Box>

			{/* CAST */}
			<Box borderTop="1px solid" borderColor="gray.100" py={24}>
				<Container maxW="1400px" px={8}>
					<VStack align="start" gap={0} mb={12}>
						<Text
							fontSize="11px"
							fontWeight="500"
							textTransform="uppercase"
							letterSpacing="0.22em"
							color="gray.400"
						>
							Your avatars · Example
						</Text>
						<Heading
							mt={5}
							fontSize={{ base: '32px', md: '52px' }}
							fontWeight={400}
							lineHeight={1}
							letterSpacing="-0.035em"
						>
							A recurring set of characters {/*<Text as="span" color="gray.500">*/}
							{/*	your audience remembers.*/}
							{/*</Text>*/}
						</Heading>
					</VStack>

					<Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
						{cast.map((c) => (
							<Box
								key={c.name}
								as="article"
								borderRadius="3xl"
								border="1px solid"
								borderColor="gray.100"
								overflow="hidden"
								bg="white"
								shadow="sm"
								transition="all 0.3s"
								_hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
							>
								<Box aspectRatio="3/4" position="relative" overflow="hidden">
									<NextImage
										src={c.img}
										alt={`AI avatar @${c.name}`}
										fill
										style={{ objectFit: 'cover' }}
									/>
									<Box
										position="absolute"
										insetX={0}
										bottom={0}
										bg="linear-gradient(to top, rgba(0,0,0,0.7), transparent)"
										p={5}
									>
										<HStack gap={1.5}>
											<Icon as={AtSign} boxSize={3.5} color="white" />
											<Text fontFamily="mono" fontSize="14px" fontWeight="600" color="white">
												{c.name}
											</Text>
										</HStack>
										<Text fontSize="12px" color="white/80" mt={0.5}>
											{c.tone}
										</Text>
									</Box>
								</Box>
								<Box p={5}>
									<Heading fontSize="18px" fontWeight={500} letterSpacing="tight">
										{c.role}
									</Heading>
									<Text mt={1} fontFamily="mono" fontSize="12.5px" color="gray.500">
										Try:{' '}
										<Text as="span" color="blackAlpha.800">
											{c.prompt}
										</Text>
									</Text>
								</Box>
							</Box>
						))}
					</Grid>
				</Container>
			</Box>

			{/* BENEFITS */}
			<Box borderTop="1px solid" borderColor="gray.100" bg="gray.50" py={28}>
				<Container maxW="1400px" px={8}>
					<Grid templateColumns={{ base: '1fr', lg: '1fr 1.4fr' }} gap={16}>
						<VStack align="start" gap={0}>
							<Text
								fontSize="11px"
								fontWeight="500"
								textTransform="uppercase"
								letterSpacing="0.22em"
								color="gray.400"
							>
								Why AI avatars
							</Text>
							<Heading
								mt={4}
								fontSize={{ base: '32px', md: '52px' }}
								fontWeight={400}
								lineHeight={1.02}
								letterSpacing="-0.035em"
							>
								One character.
								<br />
								<Text as="span" color="gray.500">
									Infinite scenes.
								</Text>
							</Heading>
							<Text mt={5} fontSize="15px" color="gray.500" maxW="sm" lineHeight="1.5">
								Most generative tools forget your character between renders. Imagemotion lets you reuse
								characters that stay consistent — locked, reusable, and unmistakably.
							</Text>
						</VStack>

						<Grid
							templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }}
							gap="1px"
							bg="gray.200"
							borderRadius="3xl"
							overflow="hidden"
							border="1px solid"
							borderColor="gray.200"
						>
							{benefits.map((b) => (
								<Box key={b.t} bg="white" p={7}>
									<Heading mt={4} fontSize="19px" fontWeight={500} letterSpacing="tight">
										{b.t}
									</Heading>
									<Text mt={2} fontSize="14px" color="gray.500" lineHeight="tall">
										{b.d}
									</Text>
								</Box>
							))}
						</Grid>
					</Grid>
				</Container>
			</Box>

			{/* USE CASES */}
			{/*<Box borderTop="1px solid" borderColor="gray.100" py={28}>*/}
			{/*	<Container maxW="1400px" px={8}>*/}
			{/*		<VStack align="start" gap={0} mb={14}>*/}
			{/*			<Text*/}
			{/*				fontSize="11px"*/}
			{/*				fontWeight="500"*/}
			{/*				textTransform="uppercase"*/}
			{/*				letterSpacing="0.22em"*/}
			{/*				color="gray.400"*/}
			{/*			>*/}
			{/*				What you can build with avatars*/}
			{/*			</Text>*/}
			{/*			<Heading*/}
			{/*				mt={5}*/}
			{/*				fontSize={{ base: '32px', md: '52px' }}*/}
			{/*				fontWeight={400}*/}
			{/*				lineHeight={1}*/}
			{/*				letterSpacing="-0.035em"*/}
			{/*			>*/}
			{/*				Built for the moments{' '}*/}
			{/*				<Text as="span" color="gray.500">*/}
			{/*					a real cast can't show up for.*/}
			{/*				</Text>*/}
			{/*			</Heading>*/}
			{/*		</VStack>*/}

			{/*		<Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={10}>*/}
			{/*			{useCases.map((u, i) => (*/}
			{/*				<Box key={u.t}>*/}
			{/*					<Text fontFamily="mono" fontSize="12px" color="gray.400">*/}
			{/*						{String(i + 1).padStart(2, '0')}*/}
			{/*					</Text>*/}
			{/*					<Heading mt={3} fontSize="20px" fontWeight={500} letterSpacing="tight">*/}
			{/*						{u.t}*/}
			{/*					</Heading>*/}
			{/*					<Text mt={2} fontSize="14px" color="gray.500" lineHeight="tall">*/}
			{/*						{u.d}*/}
			{/*					</Text>*/}
			{/*				</Box>*/}
			{/*			))}*/}
			{/*		</Grid>*/}
			{/*	</Container>*/}
			{/*</Box>*/}

			{/* PROMPT DEMO */}
			<Box borderTop="1px solid" borderColor="gray.100" bg="gray.50" py={28}>
				<Container maxW="1400px" px={8}>
					<Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={12} alignItems="center">
						<VStack align="start" gap={0}>
							<HStack gap={2}>
								<Icon as={Wand2} boxSize={3.5} color="primary" />
								<Text
									fontSize="11px"
									fontWeight="600"
									textTransform="uppercase"
									letterSpacing="0.18em"
									color="gray.500"
								>
									The @name shorthand
								</Text>
							</HStack>
							<Heading
								mt={4}
								fontSize={{ base: '32px', md: '52px' }}
								fontWeight={400}
								lineHeight={1.02}
								letterSpacing="-0.035em"
							>
								Write less.
								<br />
								<Text as="span" color="gray.500">
									Render exactly what you meant.
								</Text>
							</Heading>
							<Text mt={5} fontSize="15.5px" color="gray.500" maxW="md" lineHeight="1.5">
								Once your avatars are built, prompting is just describing a scene. Your avatar's face,
								body, voice and personality are already baked in.
							</Text>
						</VStack>

						<Box bg="white" p={7} borderRadius="3xl" border="1px solid" borderColor="gray.100" shadow="2xl">
							{cast
								.map((caster) => caster.prompt)
								.map((p, index) => (
									<Box
										key={p}
										borderBottom={index === 2 ? 'none' : '1px solid'}
										borderColor="gray.100"
										py={4}
										_first={{ pt: 0 }}
										_last={{ pb: 0 }}
									>
										<Text fontFamily="mono" fontSize="14px" color="black" lineHeight="relaxed">
											{p}
										</Text>
									</Box>
								))}
						</Box>
					</Grid>
				</Container>
			</Box>

			{/* CTA */}
			<Box
				as="section"
				position="relative"
				overflow="hidden"
				borderTop="1px solid"
				borderColor="gray.100"
				py={32}
			>
				{/* Iridescent background effect */}
				<Box
					pointerEvents="none"
					position="absolute"
					left="50%"
					top="50%"
					w={{ base: '300px', md: '700px' }}
					h={{ base: '300px', md: '500px' }}
					transform="translate(-50%, -50%)"
					borderRadius="full"
					bg="linear-gradient(45deg, oklch(65% .18 250), oklch(75% .18 250), #BEE3F8)"
					opacity={0.3}
					filter="blur(80px)"
				/>

				<Container maxW="3xl" px={8} position="relative" zIndex={1}>
					<VStack gap={0} textAlign="center">
						<Heading
							fontSize={{ base: '40px', md: '72px' }}
							fontWeight={400}
							lineHeight={0.98}
							letterSpacing="-0.04em"
						>
							Build your avatars.
							<br />
							<Text as="span" color="gray.500">
								Create the content.
							</Text>
						</Heading>
						<Text mt={6} fontSize="16px" color="gray.500" maxW="lg" lineHeight="relaxed">
							No modeling, no designing, just AI.
						</Text>
						<Flex wrap="wrap" justify="center" align="center" gap={3} mt={9}>
							<CtaButton href={ROUTES.REGISTER} showIcon hoverScale shadow="xl">
								Open the avatar studio
							</CtaButton>
						</Flex>
					</VStack>
				</Container>
			</Box>
		</Box>
	);
}

export default Index;
